import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import "./AlternativeOfferModal.css";
import CloseIcon from "../../../../src/icons/X.svg";
import GenericClock from "../../../../src/icons/GenericClock";
import { OfferContainer } from "./OfferContainer";
import * as http from "../../../utils/http";
import * as translator from "../../../utils/translator";
import { useDragModal } from "../../../customHooks/useDragModal";
import { WAIT_CUSTOMER_DECISION } from "../../../utils/consts";

export type ProductItem = {
    order_id: number;
    product_item_id: number;
    imageSrc: string;
    price: number;
    titleView: string;
    title: string;
    catalogPage: string;
    cannabisCatalogProductProperties: CannabisData;
}

type CannabisData = {
    thc: number | null,
    cbd: number | null,
    type: string | null,
    genetic: string | null,
    strain: string | null,
}

export type ExpectedAlternativeProductData = {
    productItemID: number;
    price: number;
    thc: number;
    cbd: number;
    strain: string | null;
    titleView: string;
    imageSrc: string;
    catalogPage: string;
} | null;

export const REJECT_ALTERNATIVE = "REJECT_ALTERNATIVE" as const;
export const WAIT_FOR_RESTOCK_OF_ORIGINAL_PRODUCT = "WAIT_FOR_RESTOCK_OF_ORIGINAL_PRODUCT" as const;
export type CustomerDecisionOptionsType = typeof REJECT_ALTERNATIVE | typeof WAIT_FOR_RESTOCK_OF_ORIGINAL_PRODUCT | number | null;
type CustomerDecisionsType = { originalProductItemID: number, alternativeDecision: CustomerDecisionOptionsType }[];

interface AlternativeOfferModalProps {
    closeModal: () => void;
    orderProducts: { order_id: number, product_item_id: number }[]; // IMPORTANT: orderProducts is expected to be an array of length 1 or more, of only RX products (no OTC), all of which are out of stock, with the exception of cannabisID.
    setRefreshData: (cb: (prev: boolean) => boolean) => void;
    setStickyNotificationDetails?: (details: any) => void;
}

const AlternativeOfferModal: React.FC<AlternativeOfferModalProps> = ({ closeModal, orderProducts, setRefreshData, setStickyNotificationDetails }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [productAlternativePairs, setProductAlternativePairs] = useState<([ProductItem, ExpectedAlternativeProductData][]) | null>(null);
    const [customerDecisions, setCustomerDecisions] = useState<CustomerDecisionsType>([]);
    const [treatAsMultipleProductsOrder, setTreatAsMultipleProductsOrder] = useState(false); // used for textual purposes only , indactaive of the original order group to include other products and (RX, cannabisID, OTC, etc.)
    const [orderStatus, setOrderStatus] = useState<string | null>(null);
    const hasMultipleOutOfStockProducts = useRef(false);  // used to distinguish between layouts for single alternative and multiple alternatives 
    const hasAnyAlternatives = useRef(false); // tracks if any alternatives exist on every fetch
    const customerHasPendingDecisions = customerDecisions.some(decision => decision.alternativeDecision === null);
    const { country } = useParams() as { country: string };

    // Drag functionality using useDragModal hook
    const shouldShowModal = productAlternativePairs !== null && !isLoading;
    const shouldShowBackground = isLoading || shouldShowModal; // Background should show during loading and when modal is shown
    const {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        currentTranslateY,
        modalDragTransition,
        isOpening,
        isClosing,
        closeModal: closeModalWithAnimation
    } = useDragModal({
        onClose: closeModal,
        shouldShow: shouldShowModal,
        preventBodyVerticalScroll: true
    });

    // *************
    // Process alternatives data (reusable function)
    // *************
    const processAlternativesData = (
        alternativesPairs: Record<number, ExpectedAlternativeProductData>,
        originalProductItemsData: ProductItem[],
        originalOrderComprisedOfMultipleProducts: boolean,
        orderStatus: string
    ) => {
        
        setCustomerDecisions(originalProductItemsData.map(product => ({
            originalProductItemID: product.product_item_id,
            alternativeDecision: null
        })));

        hasMultipleOutOfStockProducts.current = originalProductItemsData.length > 1;

        const pairs: [ProductItem, ExpectedAlternativeProductData][] = originalProductItemsData.map(originalProduct => [originalProduct, alternativesPairs[originalProduct.product_item_id]]);
        
        // Sort pairs so that products with alternatives appear first
        const sortedPairs = pairs.sort((a, b) => {
            const aHasAlternative = a[1] !== null;
            const bHasAlternative = b[1] !== null;
            
            if (aHasAlternative && !bHasAlternative) return -1; // a comes first
            if (!aHasAlternative && bHasAlternative) return 1;  // b comes first
            return 0; // both have same status, maintain original order
        });
        
        setProductAlternativePairs(sortedPairs);
        setTreatAsMultipleProductsOrder(originalOrderComprisedOfMultipleProducts);
        hasAnyAlternatives.current = sortedPairs.some(([_, alternative]) => alternative !== null);
        setOrderStatus(orderStatus);
    };

    // *************
    // Fetch alternatives from server
    // *************
    const fetchAlternatives = () => {
        const orderID = orderProducts[0].order_id;
        http
            .getFromServer(`account/get-alternatives-for-order/${orderID}`, [http.MIDDLEWARES.CUSTOMER_ID])
            .then((response) => {
                const data = response.data as {
                    alternativesPairs: Record<number, ExpectedAlternativeProductData>;
                    originalProductItemsData: ProductItem[];
                    originalOrderComprisedOfMultipleProducts: boolean;
                    orderStatus: string;
                };
                processAlternativesData(data.alternativesPairs, data.originalProductItemsData, data.originalOrderComprisedOfMultipleProducts, data.orderStatus);
            })
            .catch((error) => {
                let translationKey = "stickyNotifications/error/error";
                let hashID = "";
                if ((error as any)?.response?.data?.code === "not_possible_action_decision_was_made") {
                    translationKey = "stickyNotifications/error/actionDenied";
                    hashID = (error as any).hashID;
                }
                
                // Show error sticky notification to the client
                if (setStickyNotificationDetails) {
                    setStickyNotificationDetails((currentDetails: any) => ({
                        show: true,
                        title: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/error/errorTitle"),
                        content: translator.translate(translator.getLanguageByCountry(country), "my_account", translationKey).replace(":order_id", hashID),
                        level: "error",
                        showInfoIcon: true,
                        refresh: !currentDetails.refresh
                    }));
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // *************
    // Submit handler to server 
    // *************
    const onSubmit = (_: React.FormEvent | null, latestCustoemrDecision?: CustomerDecisionsType) => {
        const orderID = orderProducts[0].order_id;
        setIsLoading(true);
        const customerDecisionsToSend = latestCustoemrDecision ?? customerDecisions;

        //reformat the customerDecisionsToSend to the format expected by the server
        const reformatedCustomerDecisionsToSend = customerDecisionsToSend.map(decision => ({
            ...decision,
            alternativeDecision: (() => {
                switch (decision.alternativeDecision) {
                    case REJECT_ALTERNATIVE:
                        return null;
                    case WAIT_FOR_RESTOCK_OF_ORIGINAL_PRODUCT:
                        return decision.originalProductItemID;
                    default:
                        return decision.alternativeDecision;
                }
            })()
        }));

        http.postToServer(
                `account/cannabis-products-out-of-stock-decisions/${orderID}`,
                { customerDecisions: reformatedCustomerDecisionsToSend, 
                  originalOffer: productAlternativePairs?.map(productAlternativePair => ({
                    originalProductItemID: productAlternativePair[0].product_item_id,
                    originalProductTitle: productAlternativePair[0].titleView || "unknown title",
                    alternativeProductTitle: productAlternativePair[1]?.titleView ?? "No alternative found",
                    alternativeProductPrice: productAlternativePair[1]?.price ?? null
                  }))  
                },
                [http.MIDDLEWARES.CUSTOMER_ID],
            ).then(() => {
                // Show a sticky notification based on the server's successMessageCode
                if (setStickyNotificationDetails) {
                    let keySuffix;
                    if (treatAsMultipleProductsOrder) {
                        keySuffix = "alternative_modal_submit_multiple_decisions";
                    } else {
                        // Single product order: determine the user's choice
                        const decisions = latestCustoemrDecision ?? customerDecisions;
                        const choice = decisions[0]?.alternativeDecision;
                        if (choice === REJECT_ALTERNATIVE) {
                            keySuffix = "cancel_and_refund_alternative_single";
                        } else if (choice === WAIT_FOR_RESTOCK_OF_ORIGINAL_PRODUCT) {
                            keySuffix = "wait_10_days";
                        } else if (typeof choice === "number") {
                            keySuffix = "accept_alternative_single";
                        } else {
                            keySuffix = "alternative_modal_submit_single_decisions"; // fallback
                        }
                    }
                    
                    const translationKey = `stickyNotifications/success/${keySuffix}`;
                    
                    const message = translator.translate(
                        translator.getLanguageByCountry(country),
                        "my_account",
                        translationKey
                    ).replace(":order_id", orderID.toString());

                    setStickyNotificationDetails((currentDetails: any) => ({
                        show: true,
                        title: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/success/thank_you"),
                        content: message,
                        level: "info",
                        showInfoIcon: false,
                        refresh: !currentDetails.refresh
                    }));
                }
                // Refresh the page data after successful action
                // Close modal with a small delay to allow sticky notification to render
                setTimeout(() => {
                    setRefreshData((prev: boolean) => !prev);
                    closeModal();
                }, 600);
            })
            .catch((error) => {
                const errorCode = (error as any)?.response?.data?.code;
                let translationKey = "stickyNotifications/error/error";
                let hashID = "";
                if (errorCode === "not_possible_action_decision_was_made") {
                    translationKey = "stickyNotifications/error/actionDenied";
                    hashID = (error as any).hashID;

                } else if (errorCode === "reevaluation_of_alternative_products_required") {
                    const reevaluationData = (error as any)?.response?.data?.reevaluationData;
                    if (reevaluationData) {
                        processAlternativesData(reevaluationData.alternativesPairs, reevaluationData.originalProductItemsData, reevaluationData.originalOrderComprisedOfMultipleProducts, reevaluationData.orderStatus);
                        setIsLoading(false);
                        return;
                    }
                }


                // From this point onwards, This part should't be executed, and it would br an indication of an unwanted behaviour in the server if we reached here
                setStickyNotificationDetails && setStickyNotificationDetails((currentDetails: any) => {
                // First close any existing notification
                return { ...currentDetails, show: false };
                });
                
                // Then set the new notification after a brief delay to ensure the close animation completes
                setTimeout(() => {
                    setStickyNotificationDetails && setStickyNotificationDetails((currentDetails: any) => ({
                        show: true,
                        title: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/error/errorTitle"),
                        content: translator.translate(translator.getLanguageByCountry(country), "my_account", translationKey).replace(":order_id", hashID),
                        level: "error",
                        showInfoIcon: true,
                        refresh: !currentDetails.refresh
                    }));
                    setIsLoading(false);
                }, 100);
            })
    }

    // on mount, fetch alternatives
    useEffect(() => {
        fetchAlternatives();
    }, []);

    // *************
    // Event handlers
    // *************
    const handleCustomerChoice = (originalProductItemID: number, alternativeProductItemID: CustomerDecisionOptionsType) => {
        const newCustomerChoices = customerDecisions.map(choice => {
            if (choice.originalProductItemID === originalProductItemID) {
                return { ...choice, alternativeDecision: alternativeProductItemID };
            }
            return choice;
        });
        setCustomerDecisions(newCustomerChoices);

        //  single item orders should be submitted immediately, without allowing the user to change thier decision
        if (!hasMultipleOutOfStockProducts.current && orderStatus !== WAIT_CUSTOMER_DECISION) {
            onSubmit(null, newCustomerChoices);
        }
    }

    // *************
    // Get main modal title
    // *************
    const getModalTitle = () => {
        const titleKeys = {
            multiple: {
                withAlternatives: "MyOrders/details/alternative_offer_modal/main_title_multiple_products_default",
                withoutAlternatives: "MyOrders/details/alternative_offer_modal/main_title_multiple_products_no_alternatives_available"
            },
            single: {
                withAlternatives: "MyOrders/details/alternative_offer_modal/main_title_single_product_default",
                withoutAlternatives: "MyOrders/details/alternative_offer_modal/main_title_single_product_no_alternatives_available"
            }
        };

        const orderType = treatAsMultipleProductsOrder ? 'multiple' : 'single';
        const alternativeStatus = hasAnyAlternatives.current ? 'withAlternatives' : 'withoutAlternatives';

        const isOutOfStockFlow = orderStatus === WAIT_CUSTOMER_DECISION

        return translator.translate(
            translator.getLanguageByCountry(country),
            "my_account",
            customerHasPendingDecisions || isOutOfStockFlow ? titleKeys[orderType][alternativeStatus] : "MyOrders/details/alternative_offer_modal/main_title_all_product_decisions_made"
        );
    };

    return (
        <div 
            className={`alternative-offer-modal-background ${shouldShowBackground && !isClosing ? (isLoading || isOpening ? 'opening' : 'visible') : ''} ${isClosing ? 'closing' : ''}`}
            onClick={closeModalWithAnimation}
        >
            {isLoading && (
                <div className="loading-container">
                    <div className="loading loading-circle"></div>
                </div>
            )}
            {productAlternativePairs && !isLoading && (
                <div
                    id="alternative-offer-modal-container"
                    className={`alternative-offer-modal-container ${isClosing ? 'closing' : ''}`}
                    onClick={e => e.stopPropagation()}
                    style={{
                        transform: `translateY(${currentTranslateY}px)`,
                        transition: modalDragTransition
                    }}
                >
                    {/* header of modal */}
                    <div className="alternative-offer-modal-header">
                        {/* close button */}
                        <button className="out-of-stock-close-button" onClick={closeModalWithAnimation}>
                            <img src={CloseIcon} alt="Close" />
                        </button>

                        {/* drag bar used to close the modal in mobile layout */}
                        <div
                            className="open-close-drag-bar"
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                        </div>

                        {/* "Dringend: Bestell-Update" = Urgent: Order update - only shown in desktop layout */}
                        <div className="top-attention-sign desktop-only">
                            {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/urgent_order_update")}
                        </div>
                    </div>

                    {/* body of modal - scrollable */}
                    <div className="alternative-offer-modal-main-content">

                        {/* "Dringend: Bestell-Update" = Urgent: Order update - only shown in mobile layout */}
                        <div className="top-attention-sign mobile-only">
                            {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/urgent_order_update")}
                        </div>

                        {/* top most title */}
                        <h1 className="alternative-offer-modal-main-title">
                            {getModalTitle()}
                        </h1>

                        {/* sub title - only shown if the order status is WAIT_CUSTOMER_DECISION (Non-Cannabis orders) */}
                        {orderStatus === WAIT_CUSTOMER_DECISION && (
                            <h2 className="alternative-offer-modal-sub-title">
                                {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/sub_title_multiple_products_default")}
                            </h2>
                        )}

                        {/* Show these elements only if there's at least one alternative product */}
                        {(hasAnyAlternatives.current && customerHasPendingDecisions) ?  (
                            <>  
                                {/* limited available green box with clock icon */}
                                    <div
                                        className="out-of-stock-limited-availablity-box"
                                    >
                                        <div className="limited-availablity-header">
                                            {/* clock icon */}
                                            <div className="limited-availablity-icon">
                                                {<GenericClock color="#0A9281" size={20} />}
                                            </div>

                                            {/* "Limited availability" = "Begrenzte Verfügbarkeit!" title */}
                                            <p className="limited-availablity-title">{translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/limited_availability_title")}</p>
                                        </div>

                                        <p className="limited-availablity-description">{translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/limited_availability_description")}</p>
                                    </div>

                                {/* instructions / invite to select from options */}
                                <p className="out-of-stock-offer-description">{translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/same_genetic_alternative_sub_title")}</p>
                            </>
                        ) : null}

                        {/* options area - scrollable*/}
                        <div className="out-off-stock-all-offers">
                            {productAlternativePairs.map(([originalProduct, alternativeOffer]) => {
                                return (
                                    <OfferContainer
                                        key={originalProduct.product_item_id}
                                        costumerSelectedChoice={customerDecisions.find(choice => choice.originalProductItemID === originalProduct.product_item_id)!}
                                        onCustomerChoiceChange={handleCustomerChoice}
                                        originalProduct={originalProduct}
                                        alterantiveProduct={alternativeOffer}
                                        hasMultipleOutOfStockProducts={hasMultipleOutOfStockProducts.current}
                                        treatAsMultipleProductsOrder={treatAsMultipleProductsOrder}
                                        orderStatus={orderStatus ?? ""}
                                    />
                                );
                            })}
                        </div>

                        {/* disclaimer about pricing */}
                        {hasAnyAlternatives.current && (
                            <div className="out-of-stock-disclaimer">
                                {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/disclaimer")}
                            </div>
                        )}
                    </div>

                    {/* submit - only shown for orders with multiple items that are all out of stock, and if the customer has made a decision for all items */}
                    {(hasMultipleOutOfStockProducts.current || orderStatus === WAIT_CUSTOMER_DECISION) && (
                        <div className="confirmation-section">
                            {!customerHasPendingDecisions &&
                            <p className="submit-button-description">
                                {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/submit_button_description")}
                            </p>
                            }
                            <button
                                className="accept-offer-button"
                                id="out-of-stock-confirmation-button"
                                onClick={onSubmit}
                                disabled={customerHasPendingDecisions}
                            >
                                {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/submit_button_text")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}


export default AlternativeOfferModal;